import CloseIcon from '@mui/icons-material/Close';
import { Checkbox, Chip, IconButton } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { Option } from '../../utils/countryUtils';
import {
  cityLabelMap,
  cityOptions,
  cityPostalCodeMap,
  countryOptions,
  dmaOptions,
  getLocationLabel,
  getStateName,
  stateOptions,
} from '../../utils/countryUtils';
import VNTextField from '../VNTextField/VNTextField';
import { GEOGRAPHY_LEVELS } from './GeoblockConstants';

type Props = {
  geographyLevel: string;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  existingValues: string[];
};

const MAX_VISIBLE = 50;

const getParentLabel = (option: Option, level: string): string | null => {
  const parts = option.value.split('-');
  const countryCode = parts[0];
  const countryName = countryOptions.find((opt) => opt.value === countryCode)?.label || countryCode;

  switch (level) {
    case 'Subdivision':
      return countryName;
    case 'Dma':
    case 'City':
    case 'PostalCode': {
      if (parts.length >= 2) {
        const stateCode = `${countryCode}-${parts[1]}`;
        const stateName = stateOptions.find((opt) => opt.value === stateCode)?.label || parts[1];
        return `${stateName}, ${countryName}`;
      }
      return countryName;
    }
    default:
      return null;
  }
};

const getOptionsForLevel = (level: string): Option[] => {
  switch (level) {
    case 'Country':
      return countryOptions;
    case 'Subdivision':
      return stateOptions;
    case 'Dma':
      return dmaOptions;
    case 'City':
      return cityOptions;
    default:
      return [];
  }
};

const GeoblockLocationBrowser = ({ geographyLevel, selectedValues, onSelectionChange, existingValues }: Props) => {
  const { watch, setValue } = useFormContext();
  const search = watch('tmp.geoblock.search') || '';
  const selectedCity = watch('tmp.geoblock.selectedCity') || '';

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isPostalCode = useMemo(() => geographyLevel === 'PostalCode', [geographyLevel]);
  const allOptions = useMemo(() => getOptionsForLevel(geographyLevel), [geographyLevel]);
  const availableOptions = useMemo(
    () => allOptions.filter((opt) => !existingValues.includes(opt.value)),
    [allOptions, existingValues]
  );

  // City search options for PostalCode mode (de-duplicated from map keys)
  const citySearchOptions = useMemo(() => {
    if (!isPostalCode) {
      return [];
    }
    const options: Option[] = Array.from(cityPostalCodeMap.keys()).map((cityValue) => {
      const label = cityLabelMap.get(cityValue) || cityValue;
      return { value: cityValue, label };
    });

    return options;
  }, [isPostalCode]);

  // Available zip codes for the selected city
  const availableZipCodes = useMemo(() => {
    if (!isPostalCode || !selectedCity) {
      return [];
    }
    const allZips = cityPostalCodeMap.get(selectedCity) || [];
    return allZips.filter((zip) => !existingValues.includes(zip));
  }, [isPostalCode, selectedCity, existingValues]);

  // Selected city display info
  const selectedCityInfo = useMemo(() => {
    if (!selectedCity) {
      return null;
    }
    const label = cityLabelMap.get(selectedCity) || '';
    const parts = selectedCity.split('-');
    const cityName = label.split(',')[0]?.trim() || parts.slice(2).join('-').replace(/_/g, ' ');
    const stateCode = parts.length >= 2 ? `${parts[0]}-${parts[1]}` : '';
    const stateName = getStateName(stateCode) || parts[1] || '';
    return { cityName, subtitle: `${stateName}, United States`, label };
  }, [selectedCity]);

  const allSearchResults = useMemo(() => {
    if (isPostalCode) {
      // In PostalCode mode, search filters cities
      if (!search.trim()) {
        return citySearchOptions;
      }
      const lower = search.toLowerCase();
      return citySearchOptions.filter((opt) => opt.label.toLowerCase().includes(lower));
    }
    if (!search.trim()) {
      return availableOptions;
    }
    const lower = search.toLowerCase();
    return availableOptions.filter((opt) => opt.label.toLowerCase().includes(lower));
  }, [availableOptions, search, isPostalCode, citySearchOptions]);

  const filteredOptions = useMemo(() => allSearchResults.slice(0, MAX_VISIBLE), [allSearchResults]);
  const levelLabel = useMemo(() => GEOGRAPHY_LEVELS[geographyLevel]?.plural || 'Locations', [geographyLevel]);

  const totalSearchResults = allSearchResults.length;

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  const selectAllResults = () => {
    const newValues = allSearchResults.map((opt) => opt.value).filter((v) => !selectedValues.includes(v));
    if (newValues.length > 0) {
      onSelectionChange([...selectedValues, ...newValues]);
    }
  };

  const handleRemoveSelected = (value: string) => {
    onSelectionChange(selectedValues.filter((v) => v !== value));
  };

  const handleSelectCity = (cityValue: string) => {
    setValue('tmp.geoblock.selectedCity', cityValue);
    setValue('tmp.geoblock.search', '');
    setDropdownOpen(false);
  };

  const handleClearCity = () => {
    setValue('tmp.geoblock.selectedCity', '');
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Reset search when geography level changes
  useEffect(() => {
    setValue('tmp.geoblock.search', '');
    setValue('tmp.geoblock.selectedCity', '');
    setDropdownOpen(false);
  }, [geographyLevel]);

  // PostalCode mode with a city selected: show city card + zip code list
  if (isPostalCode && selectedCity && selectedCityInfo) {
    return (
      <div className="location-browser" ref={containerRef}>
        <span className="postal-code-section-label">Selected City</span>
        <div className="selected-city-card">
          <div className="city-card-info">
            <span className="city-card-name">{selectedCityInfo.cityName}</span>
            <span className="city-card-subtitle">{selectedCityInfo.subtitle}</span>
          </div>
          <IconButton size="small" className="city-card-close" onClick={handleClearCity}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className="postal-code-list">
          <span className="postal-code-list-header">
            Available {levelLabel} ({availableZipCodes.length})
          </span>
          <div className="postal-code-list-items">
            {availableZipCodes.map((zipValue) => {
              const zipCode = zipValue.split('-').slice(2).join('-');
              const isSelected = selectedValues.includes(zipValue);
              return (
                <button
                  key={zipValue}
                  type="button"
                  className={`postal-code-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleToggle(zipValue)}
                >
                  <div className="postal-code-left">
                    <Checkbox size="small" checked={isSelected} tabIndex={-1} />
                    <span className="zip-code-label">{zipCode}</span>
                  </div>
                  <span className="postal-code-right">{selectedCityInfo?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Default mode (all levels) + PostalCode without city selected (city search)
  return (
    <div className="location-browser" ref={containerRef}>
      <div className="browser-header">
        {dropdownOpen && totalSearchResults > 0 && !isPostalCode && totalSearchResults < 50 && (
          <button type="button" className="browser-toggle" onClick={selectAllResults}>
            Select all results{totalSearchResults > 0 ? ` (${totalSearchResults})` : ''}
          </button>
        )}
      </div>

      <VNTextField
        label={isPostalCode ? 'Browse Cities' : 'Browse Locations'}
        name="tmp.geoblock.search"
        placeholders={isPostalCode ? ['Search cities...'] : [`🔍  Search ${levelLabel}...`]}
        onFocus={() => setDropdownOpen(true)}
        shrinkLabel
      />

      {dropdownOpen && (
        <div className="browser-dropdown-wrapper">
          <div className="browser-dropdown">
            <div className="browser-dropdown-header">
              <span>
                {totalSearchResults > 0 && (
                  <>
                    {totalSearchResults} {isPostalCode ? 'Cities' : levelLabel} available
                  </>
                )}
              </span>
              <IconButton size="small" onClick={() => setDropdownOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <div className="browser-dropdown-list">
              {filteredOptions?.map((opt) => {
                if (isPostalCode) {
                  // City items in PostalCode mode — no checkbox, single-select
                  const parentLabel = getParentLabel(opt, 'City');
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className="browser-list-item"
                      onClick={() => handleSelectCity(opt.value)}
                    >
                      <div className="item-info">
                        <span className="item-label">{opt.label}</span>
                        {parentLabel && <span className="item-sublabel">{parentLabel}</span>}
                      </div>
                    </button>
                  );
                }

                const isSelected = selectedValues.includes(opt.value);
                const parentLabel = getParentLabel(opt, geographyLevel);

                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`browser-list-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggle(opt.value)}
                  >
                    <Checkbox size="small" checked={isSelected} tabIndex={-1} />
                    {opt.flag && <span className="country-flag">{opt.flag}</span>}
                    <div className="item-info">
                      <span className="item-label">{opt.label}</span>
                      {parentLabel && <span className="item-sublabel">{parentLabel}</span>}
                    </div>
                  </button>
                );
              })}
              {totalSearchResults === 0 && (
                <div className="browser-empty">{isPostalCode ? 'No cities found' : `No ${levelLabel} found`}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedValues.length > 0 && !isPostalCode && (
        <div className="browser-selected-chips">
          <span className="selected-chips-label">Selected locations ({selectedValues.length})</span>
          <div className="selected-chips-list">
            {selectedValues.map((value) => (
              <Chip
                key={value}
                label={getLocationLabel(value, geographyLevel)}
                color="primary"
                onDelete={() => handleRemoveSelected(value)}
                deleteIcon={<CloseIcon fontSize="small" />}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeoblockLocationBrowser;
